// Ported from dev-scrips-pm-react src/scheduling/lib/drop-routing.ts (PROD-1190 calendar port; re-synced PROD-3313).
/**
 * Pure decision helper for drag-drop reschedule routing in the calendar
 * page. Extracted so the routing rules (missing-context error,
 * duration-preserving end computation) can be exercised by unit tests
 * without rendering the whole page + every service hook.
 *
 * Drag-drop reschedules the SINGLE occurrence — recurring or not (Flutter
 * parity: `QuickRescheduleAppointmentEvent` → PUT /Appointment/Reschedule
 * with just {id,start,end}; no scope prompt on drag). Series-wide changes
 * live in the recurring-series edit menu, not the drag gesture. The PA used
 * to defer a recurring drop to a scope dialog, whose Following / All moved
 * the whole series (Andrew's review, PROD-3313).
 *
 * The page-level handler dispatches on the returned `kind`:
 *   - `error`   — the source row is missing essential fields, or the visit is
 *                 Cancelled / NoShow (the calendar loads those since PROD-3313,
 *                 and a moved cancelled visit is not a reschedule); toast + skip.
 *   - `this-only` — fire the single-occurrence Reschedule with the computed
 *                 start / end pair.
 */

import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import type { AppointmentsDetailsResponse } from '@/types/scheduling-api';

const NOT_MOVABLE = new Set(['cancelled', 'canceled', 'noshow', 'no-show']);

export interface DropInput {
  id: string;
  newStartISO: string;
}

export type DropRoute =
  | { kind: 'error'; message: string }
  | {
      kind: 'this-only';
      payload: { id: string; newStartISO: string; newEndISO: string };
    };

export interface RouteDropParams {
  /** Cached day-appointments list — used to look up the source row. */
  dayAppointments: AppointmentsDetailsResponse | undefined;
  /** The drop event from the grid. */
  event: DropInput;
}

export function routeAppointmentDrop(params: RouteDropParams): DropRoute {
  const { dayAppointments, event } = params;

  const apt = dayAppointments?.appointments?.find((a) => a.id === event.id);
  if (!apt?.start || !apt?.end) {
    return { kind: 'error', message: 'Cannot reschedule — missing source times.' };
  }
  const statusCode = (apt.status as { code?: string } | undefined)?.code?.toLowerCase() ?? '';
  if (NOT_MOVABLE.has(statusCode)) {
    return { kind: 'error', message: "A cancelled or no-show visit can't be moved." };
  }

  const durationMs = new Date(apt.end).getTime() - new Date(apt.start).getTime();
  const newEnd = new Date(new Date(event.newStartISO).getTime() + durationMs);
  // Local wire time, no `Z`, the same shape as `newStartISO` (week-layout); `toISOString()` sent UTC.
  const newEndISO = formatLocalDatetimeForWire(newEnd);

  return {
    kind: 'this-only',
    payload: { id: event.id, newStartISO: event.newStartISO, newEndISO },
  };
}
